/**
 * pf-search.js
 *
 * Predictive search drawer for the pf-1842 theme. Registers the
 * <pf-search-drawer> custom element and exposes a global controller:
 *
 *   window.pfSearch.open()   // open the drawer (called by pf-header)
 *   window.pfSearch.close()  // close the drawer
 *
 * The element fetches /search/suggest.json on input (debounced), renders
 * products / suggestions / collections / pages into tabs that only show
 * when their category has results, persists recent searches in
 * localStorage, and aborts in-flight fetches when the input changes.
 *
 * Loaded once from layout/theme.liquid with `defer`. Pairs with the
 * search button in pf-header which calls window.pfSearch.open().
 */

(function () {
  if (customElements.get('pf-search-drawer')) return;

  const STORAGE_KEY = 'pf-recent-searches';

  const TAB_LABELS = {
    products: 'PRODUCTS',
    queries: 'SUGGESTIONS',
    collections: 'COLLECTIONS',
    pages: 'PAGES',
  };

  function getCurrencyCode() {
    return (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
  }

  function getLocale() {
    return (window.Shopify && window.Shopify.locale) || document.documentElement.lang || 'en';
  }

  function formatMoney(cents) {
    if (cents == null) return '';
    const code = getCurrencyCode();
    const locale = getLocale();
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: code,
        minimumFractionDigits: 2,
      }).format(parseFloat(cents) / 100);
    } catch (e) {
      return '€' + (parseFloat(cents) / 100).toFixed(2);
    }
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // Strip everything but <mark> from styled_text so we don't trust the
  // server's HTML payload entirely. Shopify produces clean markup but we
  // sanitize defensively.
  function sanitizeStyledText(raw) {
    if (!raw) return '';
    // Replace <mark> opens/closes with placeholders, escape the rest,
    // then restore the marks.
    const placeholderOpen = 'MK_OPEN';
    const placeholderClose = 'MK_CLOSE';
    const replaced = String(raw)
      .replace(/<mark[^>]*>/gi, placeholderOpen)
      .replace(/<\/mark>/gi, placeholderClose);
    const escaped = escapeHtml(replaced);
    return escaped
      .split(placeholderOpen).join('<mark>')
      .split(placeholderClose).join('</mark>');
  }

  function readRecent() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.filter((x) => typeof x === 'string') : [];
    } catch (e) {
      return [];
    }
  }

  function writeRecent(list) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (e) { /* quota exceeded / disabled — ignore */ }
  }

  class PfSearchDrawer extends HTMLElement {
    connectedCallback() {
      this.recentEnabled = this.dataset.recentEnabled === 'true';
      this.recentLimit = parseInt(this.dataset.recentLimit, 10) || 5;
      this.debounceMs = parseInt(this.dataset.debounceMs, 10) || 250;
      this.minChars = parseInt(this.dataset.minChars, 10) || 2;

      this.refs = {
        backdrop: this.querySelector('[data-pf-search-backdrop]'),
        panel: this.querySelector('.pf-search-drawer__panel'),
        form: this.querySelector('[data-pf-search-form]'),
        input: this.querySelector('[data-pf-search-input]'),
        clearAll: this.querySelector('[data-pf-search-clear-all]'),
        close: this.querySelector('[data-pf-search-close]'),
        body: this.querySelector('[data-pf-search-body]'),
        recent: this.querySelector('[data-pf-search-recent]'),
        recentList: this.querySelector('[data-pf-search-recent-list]'),
        loading: this.querySelector('[data-pf-search-loading]'),
        results: this.querySelector('[data-pf-search-results]'),
        tabs: this.querySelector('[data-pf-search-tabs]'),
        panels: this.querySelector('[data-pf-search-panels]'),
        viewAll: this.querySelector('[data-pf-search-view-all]'),
        noResults: this.querySelector('[data-pf-search-no-results]'),
      };

      this.abortController = new AbortController();
      this.fetchController = null;
      this.activeTab = null;

      this.bindEvents();
      this.exposeGlobalApi();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.fetchController?.abort();
      this.unlockScroll();
      if (window.pfSearch && window.pfSearch._owner === this) {
        delete window.pfSearch;
      }
    }

    exposeGlobalApi() {
      const api = {
        _owner: this,
        open: () => this.open(),
        close: () => this.close(),
        isOpen: () => this.classList.contains('is-open'),
      };
      window.pfSearch = api;
    }

    // Show the top-bar CLEAR button when either the input has content
    // OR there are recent searches saved. Called from every transition
    // (open, input, render-recent) so the button stays in sync.
    updateClearAllVisibility() {
      if (!this.refs.clearAll) return;
      const hasInput = !!(this.refs.input && this.refs.input.value);
      const hasRecent = this.recentEnabled && readRecent().length > 0;
      this.refs.clearAll.hidden = !(hasInput || hasRecent);
    }

    bindEvents() {
      const { signal } = this.abortController;

      this.refs.close?.addEventListener('click', () => this.close(), { signal });
      this.refs.backdrop?.addEventListener('click', () => this.close(), { signal });

      document.addEventListener(
        'keydown',
        (e) => {
          if (e.key === 'Escape' && this.classList.contains('is-open')) {
            this.close();
          }
        },
        { signal }
      );

      // Input — debounced predictive fetch.
      let timer = null;
      this.refs.input?.addEventListener(
        'input',
        () => {
          const value = this.refs.input.value;
          this.updateClearAllVisibility();

          if (timer) clearTimeout(timer);
          if (value.trim().length < this.minChars) {
            // Below threshold — abort any in-flight, restore idle state.
            this.fetchController?.abort();
            this.fetchController = null;
            this.showIdle();
            return;
          }
          timer = setTimeout(() => this.runSearch(value.trim()), this.debounceMs);
        },
        { signal }
      );

      // "CLEAR" text button — clears whichever of input + recent searches
      // is currently present. Replaces the prior inline X icon that lived
      // inside the input and visually conflicted with the close X.
      this.refs.clearAll?.addEventListener(
        'click',
        () => {
          if (this.refs.input.value) {
            this.refs.input.value = '';
            this.fetchController?.abort();
            this.fetchController = null;
          }
          writeRecent([]);
          this.showIdle();
          this.updateClearAllVisibility();
          this.refs.input.focus();
        },
        { signal }
      );

      // Submitting the form (Enter key) saves the query and lets the
      // browser navigate to /search?q=...
      this.refs.form?.addEventListener(
        'submit',
        () => {
          const q = this.refs.input.value.trim();
          if (q) this.saveRecent(q);
        },
        { signal }
      );
    }

    // ---- Open / close ----
    open() {
      if (this.classList.contains('is-open')) return;
      this.hidden = false;
      void this.offsetHeight;
      this.classList.add('is-open');
      this.lockScroll();
      // Initial state — recent if available, else empty body.
      this.refs.input.value = '';
      this.showIdle();
      this.updateClearAllVisibility();
      // Focus input after the transition starts so iOS doesn't fight.
      setTimeout(() => this.refs.input.focus(), 100);
    }

    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.unlockScroll();
      this.fetchController?.abort();
      this.fetchController = null;

      const onEnd = () => {
        this.hidden = true;
        this.refs.panel?.removeEventListener('transitionend', onEnd);
      };
      if (this.refs.panel) {
        this.refs.panel.addEventListener('transitionend', onEnd, { once: true, signal: this.abortController.signal });
        setTimeout(onEnd, 340);
      } else {
        this.hidden = true;
      }
    }

    lockScroll() {
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
    }
    unlockScroll() {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    }

    // ---- View state ----
    showIdle() {
      this.refs.loading.hidden = true;
      this.refs.results.hidden = true;
      this.refs.noResults.hidden = true;
      this.refs.viewAll.hidden = true;
      const recentList = this.recentEnabled ? readRecent() : [];
      this.renderRecent(recentList);
    }

    showLoading() {
      this.refs.recent.hidden = true;
      this.refs.results.hidden = true;
      this.refs.noResults.hidden = true;
      this.refs.viewAll.hidden = true;
      this.refs.loading.hidden = false;
    }

    showResults(query, data) {
      this.refs.recent.hidden = true;
      this.refs.loading.hidden = true;
      this.refs.noResults.hidden = true;

      const results = (data && data.resources && data.resources.results) || {};
      const products = Array.isArray(results.products) ? results.products : [];
      const queries = Array.isArray(results.queries) ? results.queries : [];
      const collections = Array.isArray(results.collections) ? results.collections : [];
      const pages = Array.isArray(results.pages) ? results.pages : [];

      const total = products.length + queries.length + collections.length + pages.length;
      if (total === 0) {
        this.refs.results.hidden = true;
        this.refs.noResults.hidden = false;
        this.refs.viewAll.hidden = false;
        this.refs.viewAll.href = '/search?q=' + encodeURIComponent(query);
        return;
      }

      this.refs.results.hidden = false;
      this.refs.viewAll.hidden = false;
      this.refs.viewAll.href = '/search?q=' + encodeURIComponent(query);

      // Build tab list — only categories with results.
      const order = ['products', 'queries', 'collections', 'pages'];
      const counts = { products: products.length, queries: queries.length, collections: collections.length, pages: pages.length };
      this.refs.tabs.innerHTML = order
        .filter((k) => counts[k] > 0)
        .map((k) => {
          const isFirst = order.findIndex((kk) => counts[kk] > 0) === order.indexOf(k);
          return `<button type="button" role="tab" class="pf-search-drawer__tab" data-tab="${k}" aria-selected="${isFirst ? 'true' : 'false'}">${TAB_LABELS[k]}</button>`;
        })
        .join('');

      // Render each panel; only show the first non-empty one.
      this.renderProducts(products);
      this.renderQueries(queries, query);
      this.renderCollections(collections);
      this.renderPages(pages);

      const firstActive = order.find((k) => counts[k] > 0);
      this.activeTab = firstActive;
      this.showPanel(firstActive);

      // Bind tab clicks.
      this.refs.tabs.querySelectorAll('.pf-search-drawer__tab').forEach((btn) => {
        btn.addEventListener('click', () => {
          this.refs.tabs.querySelectorAll('.pf-search-drawer__tab').forEach((b) => b.setAttribute('aria-selected', 'false'));
          btn.setAttribute('aria-selected', 'true');
          this.activeTab = btn.dataset.tab;
          this.showPanel(this.activeTab);
        }, { signal: this.abortController.signal });
      });
    }

    showPanel(tab) {
      this.refs.panels.querySelectorAll('[data-pf-search-panel]').forEach((el) => {
        el.hidden = el.dataset.pfSearchPanel !== tab;
      });
    }

    renderProducts(products) {
      const list = this.refs.panels.querySelector('[data-pf-search-panel="products"]');
      if (!list) return;
      list.innerHTML = products.map((p) => {
        const img = (p.image || p.featured_image || '');
        const price = formatMoney(p.price);
        const compare = p.compare_at_price && parseFloat(p.compare_at_price) > parseFloat(p.price)
          ? formatMoney(p.compare_at_price)
          : '';
        return `
          <li class="pf-search-drawer__product">
            <a class="pf-search-drawer__product-link" href="${escapeHtml(p.url)}">
              ${img ? `<img class="pf-search-drawer__product-image" src="${escapeHtml(img)}" alt="${escapeHtml(p.title || '')}" loading="lazy" width="64" height="64">` : `<span class="pf-search-drawer__product-image" aria-hidden="true"></span>`}
              <div class="pf-search-drawer__product-info">
                <h4 class="pf-search-drawer__product-title">${escapeHtml(p.title || '')}</h4>
                <div class="pf-search-drawer__product-prices">
                  ${compare ? `<span class="pf-search-drawer__product-price--compare">${compare}</span>` : ''}
                  <span class="pf-search-drawer__product-price--sale">${price}</span>
                </div>
              </div>
            </a>
          </li>
        `;
      }).join('');
    }

    renderQueries(queries, currentQuery) {
      const list = this.refs.panels.querySelector('[data-pf-search-panel="queries"]');
      if (!list) return;
      list.innerHTML = queries.map((q) => {
        const styled = q.styled_text ? sanitizeStyledText(q.styled_text) : escapeHtml(q.text || '');
        const url = q.url || ('/search?q=' + encodeURIComponent(q.text || currentQuery));
        return `
          <li class="pf-search-drawer__suggestion">
            <a class="pf-search-drawer__suggestion-link" href="${escapeHtml(url)}" data-query="${escapeHtml(q.text || '')}">${styled}</a>
          </li>
        `;
      }).join('');

      // Save the queried text to recent searches on click.
      list.querySelectorAll('[data-query]').forEach((a) => {
        a.addEventListener('click', () => {
          const text = a.getAttribute('data-query');
          if (text) this.saveRecent(text);
        }, { signal: this.abortController.signal });
      });
    }

    renderCollections(collections) {
      const list = this.refs.panels.querySelector('[data-pf-search-panel="collections"]');
      if (!list) return;
      list.innerHTML = collections.map((c) => {
        const img = c.image || c.featured_image || '';
        return `
          <li class="pf-search-drawer__collection">
            <a class="pf-search-drawer__collection-link" href="${escapeHtml(c.url)}">
              ${img ? `<img class="pf-search-drawer__collection-image" src="${escapeHtml(img)}" alt="${escapeHtml(c.title || '')}" loading="lazy" width="64" height="64">` : `<span class="pf-search-drawer__collection-image" aria-hidden="true"></span>`}
              <div>
                <h4 class="pf-search-drawer__collection-title">${escapeHtml(c.title || '')}</h4>
                ${c.products_count != null ? `<div class="pf-search-drawer__collection-meta">${c.products_count} products</div>` : ''}
              </div>
            </a>
          </li>
        `;
      }).join('');
    }

    renderPages(pages) {
      const list = this.refs.panels.querySelector('[data-pf-search-panel="pages"]');
      if (!list) return;
      list.innerHTML = pages.map((p) => {
        const body = p.body ? String(p.body).replace(/<[^>]+>/g, '').trim() : '';
        return `
          <li class="pf-search-drawer__page">
            <a class="pf-search-drawer__page-link" href="${escapeHtml(p.url)}">
              <h4 class="pf-search-drawer__page-title">${escapeHtml(p.title || '')}</h4>
              ${body ? `<p class="pf-search-drawer__page-description">${escapeHtml(body.slice(0, 160))}</p>` : ''}
            </a>
          </li>
        `;
      }).join('');
    }

    // ---- Recent searches ----
    renderRecent(list) {
      if (!this.recentEnabled || list.length === 0) {
        this.refs.recent.hidden = true;
        this.updateClearAllVisibility();
        return;
      }
      this.refs.recent.hidden = false;
      this.updateClearAllVisibility();

      this.refs.recentList.innerHTML = list.slice(0, this.recentLimit).map((q) => `
        <li class="pf-search-drawer__recent-item">
          <button type="button" class="pf-search-drawer__recent-link" data-recent-query="${escapeHtml(q)}">${escapeHtml(q)}</button>
          <button type="button" class="pf-search-drawer__recent-remove" aria-label="Remove" data-recent-remove="${escapeHtml(q)}">
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" aria-hidden="true" focusable="false" class="pf-icon">
              <line x1="5" y1="5" x2="11" y2="11" />
              <line x1="11" y1="5" x2="5" y2="11" />
            </svg>
          </button>
        </li>
      `).join('');

      this.refs.recentList.querySelectorAll('[data-recent-query]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const q = btn.dataset.recentQuery;
          this.refs.input.value = q;
          this.updateClearAllVisibility();
          this.runSearch(q);
          this.refs.input.focus();
        }, { signal: this.abortController.signal });
      });
      this.refs.recentList.querySelectorAll('[data-recent-remove]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const q = btn.dataset.recentRemove;
          const next = readRecent().filter((x) => x !== q);
          writeRecent(next);
          this.renderRecent(next);
        }, { signal: this.abortController.signal });
      });
    }

    saveRecent(query) {
      if (!this.recentEnabled || !query) return;
      const current = readRecent().filter((q) => q !== query);
      current.unshift(query);
      writeRecent(current.slice(0, this.recentLimit));
    }

    // ---- Fetch ----
    async runSearch(query) {
      this.fetchController?.abort();
      this.fetchController = new AbortController();
      const signal = this.fetchController.signal;

      this.showLoading();

      const url = new URL('/search/suggest.json', window.location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('resources[type]', 'product,query,collection,page');
      url.searchParams.set('resources[limit]', '6');
      url.searchParams.set('resources[options][unavailable_products]', 'last');
      url.searchParams.set('resources[options][fields]', 'title,vendor,product_type,variants.title,tag,body');

      try {
        const res = await fetch(url.toString(), { signal, headers: { 'Accept': 'application/json' } });
        if (!res.ok) throw new Error('Search request failed');
        const data = await res.json();
        if (signal.aborted) return;
        this.showResults(query, data);
      } catch (err) {
        if (err.name === 'AbortError') return;
        // On error, show no-results rather than a confusing loading spinner.
        this.refs.loading.hidden = true;
        this.refs.results.hidden = true;
        this.refs.noResults.hidden = false;
      }
    }
  }

  customElements.define('pf-search-drawer', PfSearchDrawer);
})();
